import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    type ReactNode,
} from 'react';
import { api, ApiError } from '@/api/client';
import type { User, UserSettings, AuthStatusResponse, AppConfig } from '@/api/types';
import { resolveApiPath } from '@/contexts/GoApiContext';

/**
 * Check if the session indicator cookie exists.
 * This cookie is set by the server on successful login and cleared on logout.
 * It's non-HttpOnly so JavaScript can read it to avoid unnecessary API calls.
 */
function hasSessionIndicator(): boolean {
    return document.cookie.split(';').some((c) => c.trim().startsWith('has_session='));
}

/**
 * Clear the session indicator cookie.
 * Called when the server indicates the session has expired.
 */
function clearSessionIndicator(): void {
    document.cookie = 'has_session=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
}

interface AuthContextType {
    user: User | null;
    settings: UserSettings | null;
    config: AppConfig | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    isAdmin: boolean;
    needsOnboarding: boolean;
    hasAssetCollections: boolean;
    hasDebtCollections: boolean;
    login: (email: string, password: string, faxNumber?: string) => Promise<void>;
    logout: () => Promise<void>;
    refreshUser: () => Promise<void>;
    refreshProfile: () => Promise<void>;
    updateFromBatch: (user: User, settings: UserSettings) => void;
    markOnboardingComplete: (hasAssets: boolean, hasDebts: boolean) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
    children: ReactNode;
}

interface ProfileResponse {
    user: User;
    settings: UserSettings;
    needsOnboarding: boolean;
    hasAssetCollections: boolean;
    hasDebtCollections: boolean;
    config: AppConfig;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [user, setUser] = useState<User | null>(null);
    const [settings, setSettings] = useState<UserSettings | null>(null);
    const [config, setConfig] = useState<AppConfig | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [needsOnboarding, setNeedsOnboarding] = useState(false);
    const [hasAssetCollections, setHasAssetCollections] = useState(false);
    const [hasDebtCollections, setHasDebtCollections] = useState(false);

    const refreshUser = useCallback(async () => {
        try {
            const response = await api.get<{ user: User }>('/api/me');
            setUser(response.user);
        } catch (error) {
            if (error instanceof ApiError && error.status === 401) {
                setUser(null);
                setSettings(null);
                setConfig(null);
            } else {
                throw error;
            }
        }
    }, []);

    const refreshProfile = useCallback(async () => {
        try {
            const response = await api.get<ProfileResponse>(resolveApiPath('/api/profile/me'));
            setUser(response.user);
            setSettings(response.settings);
            setConfig(response.config);
            setNeedsOnboarding(response.needsOnboarding);
            setHasAssetCollections(response.hasAssetCollections);
            setHasDebtCollections(response.hasDebtCollections);
        } catch (error) {
            if (error instanceof ApiError && error.status === 401) {
                setUser(null);
                setSettings(null);
                setConfig(null);
                setNeedsOnboarding(false);
                setHasAssetCollections(false);
                setHasDebtCollections(false);
            } else {
                throw error;
            }
        }
    }, []);

    const updateFromBatch = useCallback((user: User, settings: UserSettings) => {
        setUser(user);
        setSettings(settings);
    }, []);

    useEffect(() => {
        // Skip API call entirely if no session indicator cookie exists.
        // This avoids unnecessary requests for anonymous users who never logged in.
        if (!hasSessionIndicator()) {
            setUser(null);
            setSettings(null);
            setConfig(null);
            setIsLoading(false);
            return;
        }

        // Session indicator exists - verify with server using 200-returning endpoint.
        // This endpoint never returns 401, avoiding log pollution.
        // auth/status is lightweight (no DB queries) — just checks session validity.
        // If authenticated, immediately fetch full profile data.
        api.get<AuthStatusResponse>(resolveApiPath('/api/auth/status'))
            .then((response) => {
                if (response.authenticated) {
                    return api.get<ProfileResponse>(resolveApiPath('/api/profile/me'));
                }
                // Session expired but cookie still exists - clear it
                clearSessionIndicator();
                return undefined;
            })
            .then((profile) => {
                if (profile) {
                    setUser(profile.user);
                    setSettings(profile.settings);
                    setConfig(profile.config);
                    setNeedsOnboarding(profile.needsOnboarding);
                    setHasAssetCollections(profile.hasAssetCollections);
                    setHasDebtCollections(profile.hasDebtCollections);
                }
            })
            .catch(() => {
                // Network error or other issue - treat as unauthenticated
                clearSessionIndicator();
            })
            .finally(() => setIsLoading(false));
    }, []);

    // Listen for session expiration events from API client
    // Clear local state when session expires; SessionExpiredOverlay handles navigation
    useEffect(() => {
        const handleSessionExpired = () => {
            // Only handle if user was authenticated (avoid duplicate handling)
            if (user) {
                clearSessionIndicator();
                setUser(null);
                setSettings(null);
                setConfig(null);
                setNeedsOnboarding(false);
                setHasAssetCollections(false);
                setHasDebtCollections(false);
            }
        };

        window.addEventListener('auth:session-expired', handleSessionExpired);
        return () => {
            window.removeEventListener(
                'auth:session-expired',
                handleSessionExpired,
            );
        };
    }, [user]);

    const login = useCallback(async (email: string, password: string, faxNumber?: string) => {
        const response = await api.post<{ user: User }>('/api/login', {
            email,
            password,
            fax_number: faxNumber,
        });
        setUser(response.user);
        // Refresh to get full profile with settings
        await refreshProfile();
    }, [refreshProfile]);

    const logout = useCallback(async () => {
        try {
            await api.post('/api/logout');
        } catch (error) {
            // A 401 means the server already ended the session, so the logout succeeded.
            if (!(error instanceof ApiError && error.status === 401)) {
                throw error;
            }
        }
        clearSessionIndicator();
        setUser(null);
        setSettings(null);
        setConfig(null);
        setNeedsOnboarding(false);
        setHasAssetCollections(false);
        setHasDebtCollections(false);
    }, []);

    const markOnboardingComplete = useCallback((hasAssets: boolean, hasDebts: boolean) => {
        setNeedsOnboarding(false);
        setHasAssetCollections(hasAssets);
        setHasDebtCollections(hasDebts);
    }, []);

    const value: AuthContextType = {
        user,
        settings,
        config,
        isLoading,
        isAuthenticated: user !== null,
        isAdmin: user?.roles?.includes('ROLE_ADMIN') ?? false,
        needsOnboarding,
        hasAssetCollections,
        hasDebtCollections,
        login,
        logout,
        refreshUser,
        refreshProfile,
        updateFromBatch,
        markOnboardingComplete,
    };

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
}

export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
}
